import {
    Add,
    AssignmentTurnedInOutlined,
    DeleteOutlineOutlined,
    NoteAltOutlined,
} from "@mui/icons-material/";
import { Box, Grid, IconButton, styled } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { displayDateFormat, systemDateFormat } from "../../../../constants";
import {
    IWithClassName,
    IWithThemeProps,
} from "../../../../theme/default.theme";
import { minDate } from "../../const/historical-options";
import { FormParams } from "../../enums/form-params";
import { Steps } from "../../enums/steps";
import {
    ICustomSnapshot,
    IEditRow,
    IError,
} from "../../interface/historical-option";

enum DateOperations {
    EDIT = "edit",
    ADD = "add",
}

interface ISnapShotFrequency extends IWithClassName {
    setDuplicateRow: (date: string) => void;
    data: Array<string>;
    onSelectChange: (
        step: Steps,
        label: FormParams,
    ) => (options: Array<string> | string) => void;
}

const SnapShotFrequency = ({
    data,
    setDuplicateRow,
    onSelectChange,
    className,
}: ISnapShotFrequency) => {
    const validData = useMemo(
        () => data.filter((date) => dayjs(date, systemDateFormat).isValid()),
        [data],
    );

    const [snapshotRow, setSnapshotRow] = useState<Array<string>>(validData);
    const [error, setError] = useState<IError>();
    const [editRow, setEditRow] = useState<IEditRow>();
    const [customSnapshot, setCustomSnapshot] = useState<ICustomSnapshot>({
        isOpen: false,
        value: "",
    });
    const [tempDate, setTempDate] = useState<string | null>(null);

    useEffect(() => {
        setSnapshotRow(validData);
    }, [validData]);

    const decendingSortDateArray = (snapShotDates: Array<string>) =>
        [...snapShotDates].sort((a, b) =>
            dayjs(a).isAfter(dayjs(b)) ? -1 : 1,
        );

    const deleteSnapshotDate = (snapshotDate: string) => {
        setSnapshotRow((rows) => {
            const newRows = rows ? [...rows] : [];
            const index = newRows.indexOf(snapshotDate);
            if (index !== -1) {
                newRows.splice(index, 1);
            }
            onSelectChange(
                Steps.LIST_DOWNLOAD_OPTIONS,
                FormParams.SNAPSHOT_DATES,
            )(newRows);
            return newRows;
        });
    };

    const dateOnChange = (label: DateOperations) => (date: Dayjs | null) => {
        const isEdit = label === DateOperations.EDIT;
        const isAdd = label === DateOperations.ADD;

        const updateSnapshotRow = (rows: Array<string>) => {
            const newRows = rows ? [...rows] : [];
            if (editRow?.row !== -1) {
                newRows.splice(editRow!.row, 1);
            }
            return newRows;
        };

        const updateCustomSnapshot = (value?: string) => {
            setCustomSnapshot({
                isOpen: false,
                value,
            });
        };

        if (isEdit) {
            if (date) {
                editRow && setTempDate(date.format(systemDateFormat));
            } else {
                editRow &&
                    setSnapshotRow((rows) => updateSnapshotRow(rows || []));
            }
            return;
        }

        if (isAdd && date) {
            updateCustomSnapshot(dayjs(date).format(systemDateFormat));
            return;
        }

        updateCustomSnapshot();
    };

    const addCustomSpanshot = () => {
        if (!customSnapshot?.value?.length) {
            setError({
                customValue: "Required",
            });

            return;
        }

        if (snapshotRow?.includes(customSnapshot.value)) {
            setDuplicateRow(customSnapshot.value);
            return;
        } else {
            setSnapshotRow((rows) => {
                const newRows = rows ? [...rows] : [];
                customSnapshot.value && newRows.push(customSnapshot.value);
                const sortedRows = decendingSortDateArray(newRows);
                onSelectChange(
                    Steps.LIST_DOWNLOAD_OPTIONS,
                    FormParams.SNAPSHOT_DATES,
                )(sortedRows);
                return sortedRows;
            });
        }

        setCustomSnapshot({
            isOpen: false,
            value: undefined,
        });
    };

    const handleMonthChange = (newMonth: Dayjs, operation: DateOperations) => {
        if (operation === DateOperations.EDIT && editRow !== undefined) {
            const baseDate = tempDate
                ? dayjs(tempDate)
                : dayjs(snapshotRow[editRow.row]);
            const newDate = baseDate
                .year(newMonth.year())
                .month(newMonth.month());
            setTempDate(newDate.format(systemDateFormat));
        } else if (operation === DateOperations.ADD) {
            const baseDate = customSnapshot.value
                ? dayjs(customSnapshot.value)
                : dayjs();
            const newDate = baseDate
                .year(newMonth.year())
                .month(newMonth.month());
            setCustomSnapshot((prevState) => ({
                ...prevState,
                value: newDate.format(systemDateFormat),
            }));
        }
    };

    const handleEditRow = (
        index: number,
        isOpen: boolean,
        isEditing?: boolean,
    ) => {
        setEditRow({
            row: index,
            isOpen,
            isEditing: isEditing ?? !editRow?.isEditing,
        });
    };

    const onSave = () => {
        if (tempDate && editRow !== undefined) {
            const isDateChanged = snapshotRow[editRow.row] !== tempDate;
            const isDuplicate = snapshotRow.some(
                (date, index) => date === tempDate && index !== editRow.row,
            );

            if (isDateChanged) {
                if (isDuplicate) {
                    setDuplicateRow(tempDate);
                } else {
                    setSnapshotRow((prevRows) => {
                        const newRows = prevRows.map((row, index) =>
                            index === editRow.row ? tempDate : row,
                        );
                        onSelectChange(
                            Steps.LIST_DOWNLOAD_OPTIONS,
                            FormParams.SNAPSHOT_DATES,
                        )(newRows);
                        return newRows;
                    });
                }
            }

            setTempDate(null);
            setEditRow(undefined);
        }
    };

    return (
        <Grid item className={className}>
            <table className="snapshot-table">
                <tbody>
                    {snapshotRow?.map((row, index) => (
                        <tr key={row}>
                            <td>
                                {editRow?.row === index && editRow.isEditing ? (
                                    <DatePicker
                                        minDate={minDate}
                                        maxDate={dayjs()}
                                        open={editRow.isOpen}
                                        value={
                                            editRow?.isOpen && tempDate
                                                ? dayjs(tempDate)
                                                : dayjs(row)
                                        }
                                        slotProps={{
                                            actionBar: {
                                                actions: ["clear"],
                                            },
                                            textField: {
                                                error: !!error?.customValue,
                                                helperText: error?.customValue,
                                                size: "small",
                                                variant: "outlined",
                                                onClick: () =>
                                                    handleEditRow(
                                                        index,
                                                        false,
                                                        true,
                                                    ),
                                            },
                                        }}
                                        onClose={() => {
                                            onSave();
                                            handleEditRow(index, false, true);
                                        }}
                                        onChange={dateOnChange(
                                            DateOperations.EDIT,
                                        )}
                                        onMonthChange={(newMonth) =>
                                            handleMonthChange(
                                                newMonth,
                                                DateOperations.EDIT,
                                            )
                                        }
                                    />
                                ) : (
                                    dayjs(row).format(displayDateFormat)
                                )}
                            </td>
                            <td>
                                <Box component="span" className="action-cont">
                                    {editRow?.row === index &&
                                    editRow.isEditing ? (
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                handleEditRow(
                                                    index,
                                                    false,
                                                    false,
                                                );
                                                onSave();
                                            }}
                                        >
                                            <AssignmentTurnedInOutlined />
                                        </IconButton>
                                    ) : (
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                handleEditRow(index, true, true)
                                            }
                                        >
                                            <NoteAltOutlined />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        size="small"
                                        onClick={() => deleteSnapshotDate(row)}
                                    >
                                        <DeleteOutlineOutlined />
                                    </IconButton>
                                </Box>
                            </td>
                        </tr>
                    ))}
                    <tr>
                        <td>
                            <DatePicker
                                minDate={minDate}
                                maxDate={dayjs()}
                                value={
                                    customSnapshot.value
                                        ? dayjs(customSnapshot.value)
                                        : null
                                }
                                open={customSnapshot.isOpen}
                                onClose={() =>
                                    setCustomSnapshot((props) => ({
                                        ...props,
                                        isOpen: false,
                                    }))
                                }
                                slotProps={{
                                    actionBar: {
                                        actions: ["clear"],
                                    },
                                    textField: {
                                        size: "small",
                                        variant: "outlined",
                                        onClick: () =>
                                            setCustomSnapshot((props) => ({
                                                ...props,
                                                isOpen: true,
                                            })),
                                    },
                                }}
                                onChange={dateOnChange(DateOperations.ADD)}
                                onMonthChange={(newMonth) =>
                                    handleMonthChange(
                                        newMonth,
                                        DateOperations.ADD,
                                    )
                                }
                            />
                        </td>
                        <td>
                            <Box component="span" className="action-cont">
                                <IconButton
                                    size="small"
                                    onClick={addCustomSpanshot}
                                >
                                    <Add />
                                </IconButton>
                            </Box>
                        </td>
                    </tr>
                </tbody>
            </table>
        </Grid>
    );
};

export default styled(SnapShotFrequency)(({ theme }: IWithThemeProps) => ({
    ".snapshot-table": {
        width: "100%",
        tr: {
            height: 30,
            "&:nth-of-type(odd)": {
                backgroundColor: theme?.palette.background.default,
            },
            "&:nth-of-type(even)": {
                backgroundColor: theme?.palette.listsTable.dropdownColor,
            },
        },
    },
    ".add-icon": {
        color: theme?.palette.action.active,
    },
    ".content-inline": {
        display: "flex",
        flexDirection: "row",
        gap: 5,
    },
    ".label-center": {
        margin: "auto 0",
    },
    ".MuiButton-root": {
        height: 30,
    },
    ".number-box": {
        width: 50,
    },
    ".multi-row": {
        justifyContent: "space-between",
        gap: 5,
        width: "100%",
    },
    ".flex-grow": {
        flexGrow: 1,
    },
    ".MuiIconButton-sizeSmall": {
        padding: theme?.spacing(0, 0.5),
        svg: {
            color: theme?.palette.text.primary,
        },
    },
    ".action-cont": {
        float: "right",
    },
    ".MuiOutlinedInput-input": {
        padding: theme?.spacing(0.25, 0.5),
    },
    ".MuiOutlinedInput-root": {
        backgroundColor: theme?.palette.background.default,
    },
    ".MuiInputAdornment-root": {
        display: "none",
    },
}));
